import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

// 导入图片
import rocketImage from "../images/rectangle-410.png";
import bgMain from "../images/bg-main.png";
import idoType1Image from "../images/ido/1.png";
import idoType2Image from "../images/ido/2.png";
import idoType3Image from "../images/ido/3.png";
import idoType4Image from "../images/ido/4.png";
import ShareButtons from "./common/ShareButtons";

const getTagImage = (type) => {
  const typeMap = {
    1: idoType1Image,
    2: idoType2Image,
    3: idoType3Image,
    4: idoType4Image
  };
  return typeMap[type] || rocketImage;
};

const getTagTitle = (type) => {
  const titleMap = {
    1: "Clueless Noobs",
    2: "Casual Crypto Enjoyers",
    3: "Floor Price Champions",
    4: "Alpha Extractors"
  };
  return titleMap[type] || "Unknown Type";
};

const getTagSubTitle = (type) => {
  const titleMap = {
    1: "(0% participation)",
    2: "(<50% participation)",
    3: "(≥50% participation, sold <30% of peak)",
    4: "(≥50% participation, sold >30% of peak)"
  };
  return titleMap[type] || "Unknown Type";
};

const getTagDescription = (type) => {
  const descriptionMap = {
    1: "Can't even ape into free money? NGMI. Enjoy your e-begging on X, sucker. 🐻📉",
    2: "Out here with that 'Zen yield farming' energy. One IDO pays for KFC Crazy Thursday , two IDOs = fancy boba treat. Hustle harder, padawan. 🍜✨",
    3: "Grind 25/8/366 but paper-hand at absolute bottom. Don’t panic in the Fud",
    4: "Absolute legend detected 🤖. Your wallet's so optimized it's basically an MEV bot. Leave some alpha for the rest of us (but lowkey teach me your ways). 💸👑"
  };
  return descriptionMap[type] || "";
};

const BinanceIDODetail = () => {
  const navigate = useNavigate();
  const [bscAddress, setBscAddress] = useState("");
  const [idoData, setIdoData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showIntro, setShowIntro] = useState(true); // Controls whether to display the introduction card
  const [progress, setProgress] = useState(0);
  const [progressStatus, setProgressStatus] = useState("Analyzing...");

  // Load BSC address from localStorage
  useEffect(() => {
    const savedAddress = localStorage.getItem("bscAddress");
    if (savedAddress) {
      setBscAddress(savedAddress);
      // Don't automatically load data, wait for user to click "Start Analysis" button
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  }, []);

  // Function to fetch user IDO data
  const fetchUserIDOData = async (address) => {
    setIsLoading(true);
    setShowIntro(false); // Hide introduction card, show analysis results

    // Initialize progress
    setProgress(0);
    setProgressStatus("Analyzing...");

    // Simulate progress updates
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 95) {
          return prev;
        }
        const increment = Math.random() * 10;
        const newProgress = Math.min(prev + increment, 95);

        // Update status message based on progress
        if (newProgress < 30) {
          setProgressStatus("Analyzing wallet transactions...");
        } else if (newProgress < 60) {
          setProgressStatus("Identifying IDO participation records...");
        } else if (newProgress < 90) {
          setProgressStatus("Generating IDO analysis report...");
        }

        return newProgress;
      });
    }, 1000);

    try {
      // Correct API endpoint URL
      const apiUrl = `https://s6xvwzdvih.us-east-1.awsapprunner.com/api/ido/participation-type/bsc/${address}`;
      console.log("Fetching IDO participation data:", apiUrl);

      // 发起初始请求
      const initialResponse = await fetch(apiUrl, {
        headers: {
          accept: "application/json",
        },
      });

      if (!initialResponse.ok) {
        throw new Error(`API request failed: ${initialResponse.status}`);
      }

      const initialData = await initialResponse.json();
      console.log("Initial IDO participation data received:", initialData);

      if (initialData.code === 0 && initialData.message === "success") {
        // 如果初始响应包含task_id，开始轮询
        if (initialData.data && initialData.data.task_id) {
          const taskId = initialData.data.task_id;
          console.log("Task ID received, starting polling:", taskId);
          
          // 设置轮询间隔（秒）
          const pollInterval = 1000; //1秒
          const maxPolls = 30; // 最多轮询30次（30秒）
          let pollCount = 0;
          
          // 创建轮询函数
          const pollResult = async () => {
            if (pollCount >= maxPolls) {
              throw new Error("轮询超时，请稍后再试");
            }
            
            pollCount++;
            console.log(`轮询结果 (${pollCount}/${maxPolls})...`);
            
            try {
              const pollResponse = await fetch(apiUrl, {
                headers: {
                  accept: "application/json",
                },
              });
              
              if (!pollResponse.ok) {
                throw new Error(`轮询请求失败: ${pollResponse.status}`);
              }
              
              const pollData = await pollResponse.json();
              console.log("轮询结果:", pollData);
              
              if (pollData.code === 0 && pollData.message === "success") {
                // 检查是否有type字段
                if (pollData.data && pollData.data.type !== undefined) {
                  console.log("分析完成，获取到type:", pollData.data.type);
                  setIdoData(pollData.data);
                  clearInterval(progressInterval);
                  setProgress(100);
                  setProgressStatus("Analysis complete");
                  setTimeout(() => {
                    setIsLoading(false);
                  }, 500);
                  return true; // 轮询成功，返回true
                } else if (pollData.data && pollData.data.status === "PROCESSING") {
                  console.log("任务仍在处理中，继续轮询...");
                  return false; // 继续轮询
                }
              } else {
                throw new Error(`API返回错误: ${pollData.message}`);
              }
            } catch (error) {
              console.error("轮询过程中出错:", error);
              throw error;
            }
            
            return false; // 默认继续轮询
          };
          
          // 开始轮询
          const poll = async () => {
            try {
              const isComplete = await pollResult();
              if (isComplete) {
                console.log("轮询完成，任务已结束");
              } else {
                // 继续轮询
                setTimeout(poll, pollInterval);
              }
            } catch (error) {
              console.error("轮询失败:", error);
              setError(error.message);
              clearInterval(progressInterval);
              setIsLoading(false);
            }
          };
          
          // 启动轮询
          poll();
        } else if (initialData.data && initialData.data.type !== undefined) {
          // 如果初始响应已经包含type，直接显示结果
          console.log("Initial response already contains type:", initialData.data.type);
          setIdoData(initialData.data);
          clearInterval(progressInterval);
          setProgress(100);
          setProgressStatus("Analysis complete");
          setTimeout(() => {
            setIsLoading(false);
          }, 500);
        } else {
          // 初始响应既没有task_id也没有type
          throw new Error("API返回的数据格式不正确");
        }
      } else {
        throw new Error(`API返回错误: ${initialData.message}`);
      }
    } catch (error) {
      console.error("Error fetching IDO data:", error);
      setError(error.message);
      clearInterval(progressInterval);
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/");
  };

  // Start analysis button click handler
  const handleStartAnalysis = () => {
    if (bscAddress) {
      fetchUserIDOData(bscAddress);
    } else {
      // If no address, return to home page for user to input address
      navigate("/");
    }
  };

  // 这个函数目前没有使用，注释掉以避免lint警告
  /*
  // Format date helper
  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString();
  };

  // Calculate total tokens helper
  const calculateTotalTokens = (allocation, price) => {
    if (!allocation || !price || price === 0) return 0;
    return allocation / price;
  };
  */

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-gradient-to-br from-pink-50 to-blue-50">
      {/* Background elements */}
      <div className="fixed inset-0 overflow-hidden">
        <div
          className="absolute w-full h-full overflow-hidden"
          style={{
            backgroundImage: `url(${bgMain})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.8,
          }}
        ></div>
      </div>

      {/* Main content */}
      <div className="relative z-10 w-full max-w-md mx-auto pt-8 px-4 pb-20">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 relative">
          <button
            onClick={handleBack}
            className="text-[#1c1f25] hover:text-[#f784ff] transition-colors z-10"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <h2 className="text-[#1c1f25] text-xl font-medium absolute left-0 right-0 text-center">
            Binance IDO Trading Analysis
          </h2>
        </div>

        <AnimatePresence mode="wait">
          {!bscAddress ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-[16px] shadow-lg p-6 text-center max-w-[345px] w-full mx-auto"
            >
              <p className="text-lg mb-4 text-[#1c1f25]">
                Please connect your wallet to view your Binance IDO performance.
              </p>
              <motion.button
                onClick={() => navigate("/")}
                className="w-full bg-gradient-to-r from-[#f784ff] to-[#b784ff] text-white py-3 rounded-lg font-medium hover:opacity-90 transition-all shadow-md flex items-center justify-center"
                whileHover={{
                  scale: 1.03,
                  boxShadow: "0 10px 25px -5px rgba(247, 132, 255, 0.4)",
                }}
                whileTap={{ scale: 0.97 }}
              >
                Connect Wallet
              </motion.button>
            </motion.div>
          ) : showIntro ? (
            <motion.div
              key="main-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Binance IDO Card - Based on Figma design */}
              <div className="flex flex-col w-[345px] items-start gap-[15px] p-[15px] relative bg-white rounded-[16px] overflow-hidden shadow-lg mx-auto">
                <div className="flex flex-col items-center gap-2.5 relative self-stretch w-full flex-[0_0_auto]">
                  {/* Card Header with background image */}
                  <img
                    className="relative self-stretch w-full h-[255px] object-cover"
                    src={rocketImage}
                    alt="Binance IDO Analysis"
                  />

                  {/* Title */}
                  <p className="relative w-fit font-medium text-[#1c1f25] text-xl tracking-[0] leading-[35px] whitespace-nowrap">
                    Binance IDO Trading Analysis
                  </p>

                  {/* Subtitle */}
                  <p className="relative self-stretch font-normal text-[#676C77] text-base tracking-[0] leading-[23px]">
                    Track your performance in Binance IDO — analyze your ROI,
                    participation history, and strategy recommendations.
                  </p>
                </div>
              </div>

              {/* 开始分析按钮 */}
              <motion.button
                onClick={handleStartAnalysis}
                className="w-full bg-gradient-to-r from-[#f784ff] to-[#b784ff] text-white py-3 rounded-lg font-medium hover:opacity-90 transition-all shadow-md flex items-center justify-center mt-6 max-w-[345px] mx-auto"
                whileHover={{
                  scale: 1.03,
                  boxShadow: "0 10px 25px -5px rgba(247, 132, 255, 0.4)",
                }}
                whileTap={{ scale: 0.97 }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Analyzing...
                  </div>
                ) : (
                  "Start Analysis"
                )}
              </motion.button>
            </motion.div>
          ) : isLoading ? (
            <motion.div
              key="loading-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Binance  IDO Card - Based on Figma design */}
              <div className="flex flex-col w-[345px] items-start gap-[15px] p-[15px] relative bg-white rounded-[16px] overflow-hidden shadow-lg mx-auto">
                <div className="flex flex-col items-center gap-2.5 relative self-stretch w-full flex-[0_0_auto]">
                  {/* Card Header with background image */}
                  <img
                    className="relative self-stretch w-full h-[255px] object-cover"
                    src={rocketImage}
                    alt="Binance IDO Analysis"
                  />

                  {/* Title */}
                  <p className="relative w-fit font-medium text-[#1c1f25] text-xl tracking-[0] leading-[35px] whitespace-nowrap">
                    Binance IDO Trading Analysis
                  </p>

                  {/* Subtitle */}
                  <p className="relative self-stretch font-normal text-[#676C77] text-base tracking-[0] leading-[23px]">
                    Track your performance in Binance IDO — analyze your ROI,
                    participation history, and strategy recommendations.
                  </p>

                  {/* Progress bar inside card */}
                  <div className="w-full mt-4 flex flex-col items-center">
                    <div className="flex flex-col w-full items-start gap-2.5">
                      {/* Progress bar */}
                      <div className="flex flex-col w-full items-start gap-2.5 relative flex-[0_0_auto] bg-[#cacfdb] rounded-[100px] border-[0.5px] border-solid border-[#E5E7EB]">
                        <div
                          className="relative h-[15px] rounded-[100px]"
                          style={{
                            width: `${progress}%`,
                            background:
                              "linear-gradient(94deg, #f784ff 0%, #b784ff 100%)",
                          }}
                        />
                      </div>

                      {/* Status text */}
                      <div className="relative self-stretch font-normal text-[#959AA3] text-sm text-center tracking-[0] leading-[23px]">
                        {Math.floor(progress)}%&nbsp;&nbsp;&nbsp;&nbsp;
                        {progressStatus}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ) : error ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-[16px] shadow-lg p-6 text-center max-w-[345px] w-full mx-auto"
            >
              <p className="text-red-600 mb-4">Error fetching data: {error}</p>
              <motion.button
                onClick={() => fetchUserIDOData(bscAddress)}
                className="w-full bg-gradient-to-r from-[#f784ff] to-[#b784ff] text-white py-3 rounded-lg font-medium hover:opacity-90 transition-all shadow-md flex items-center justify-center"
                whileHover={{
                  scale: 1.03,
                  boxShadow: "0 10px 25px -5px rgba(247, 132, 255, 0.4)",
                }}
                whileTap={{ scale: 0.97 }}
              >
                Retry
              </motion.button>
            </motion.div>
          ) : idoData && idoData.type ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4 }}
              className="mt-6 w-[355px] mx-auto"
            >
              {/* 新结果卡片设计 */}
              <div
                id="ido-card"
                className="relative w-[355px] bg-[#0a0e1f] rounded-[16px] overflow-hidden border border-solid border-transparent"
              >
                {/* Tag Image */}
                <motion.div className="w-full h-auto overflow-hidden">
                  <motion.img
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    className="w-full object-contain"
                    src={getTagImage(idoData.type)}
                    alt={getTagTitle(idoData.type)}
                  />
                </motion.div>
                

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="px-7 py-5 flex flex-col space-y-4"
                >
                  {/* Title */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, delay: 0.3 }}
                    className="w-full font-bold text-[22px] text-center tracking-[0] leading-[normal] text-[#f784ff]"
                  >
                    {getTagTitle(idoData.type)}
                  </motion.div>

                   {/* Behavior Description */}
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: 0.4 }}
                      className="w-full font-normal text-[#f5f5f5] text-base tracking-[0] leading-[normal]"
                    >
                      <span className="font-medium whitespace-pre-line">
                      {getTagDescription(idoData.type)}
                      </span>
                    </motion.div>
  
                    {/* Humor Comment */}
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: 0.5 }}
                      className="w-full text-[#a0a0a0] text-sm tracking-[0] leading-[normal] border-l-2 border-[#3f51b5] pl-3 mt-2"
                    >
                      {idoData.token_profits && idoData.participated_tokens && idoData.participated_tokens.length > 0 ? (
                        <div className="space-y-2">
                          <div className="font-medium text-[#f5f5f5]">
                            Participated Tokens: <span className="font-normal text-[#a0a0a0]">{idoData.participated_tokens.join(', ')}</span>
                          </div>
                          
                          <div className="space-y-1.5 mt-1">
                            {idoData.participated_tokens.length > 0 && idoData.participated_tokens.map((token) => (
                              idoData.token_profits[token] && (
                                <div key={token} className="flex items-start">
                                  <span className="text-[#3f51b5] mr-1.5">•</span>
                                  <div>
                                    <span className="font-medium text-[#d4d4d4]">{token}</span>
                                    <span className="text-[#a0a0a0]"> Profit: </span>
                                    <span className="text-[#4caf50]">{idoData.token_profits[token].roi_formatted}</span>
                                  </div>
                                </div>
                              )
                            ))}
                          </div>

                          <div className="mt-3">
                            <div className="flex justify-between items-center mb-2">
                              <div className="text-[#d4d4d4] text-sm">IDO Participated Progress:</div>
                              <div className="text-[#f0f0f0] font-medium bg-[#2a2a2a] px-2 py-0.5 rounded-md">
                                {idoData.participated_tokens.length}/6
                                <span className="text-xs ml-1 text-[#8bc34a]">
                                  ({Math.round((idoData.participated_tokens.length / 6) * 100)}%)
                                </span>
                              </div>
                            </div>
                            
                            {/* 圆形指示器 */}
                            <div className="flex justify-between mb-4 px-1 pt-2">
                              {['kILO', 'PARTI', 'BR', 'BMT', 'SHELL', 'BID'].map((token, index) => (
                                <div key={index} className="relative flex flex-col items-center">
                                  <div 
                                    className={`w-8 h-8 rounded-full flex items-center justify-center ${index < idoData.participated_tokens.length ? 'bg-gradient-to-r from-[#4caf50] to-[#8bc34a]' : 'bg-[#2a2a2a]'} shadow-md`}
                                  >
                                    {index < idoData.participated_tokens.length && (
                                      <span className="text-white text-xs">✓</span>
                                    )}
                                  </div>
                                  <div className={`text-xs text-center mt-2 font-medium ${index < idoData.participated_tokens.length ? 'text-[#d4d4d4]' : 'text-[#a0a0a0]'}`}>{token}</div>
                                </div>
                              ))}
                            </div>
                            
                            {/* 进度条 */}
                            <div className="flex items-center mb-2">
                              <div className="flex-1 bg-[#2a2a2a] h-2 rounded-full overflow-hidden">
                                <div 
                                  className="h-full rounded-full" 
                                  style={{
                                    width: `${(idoData.participated_tokens.length / 6) * 100}%`,
                                    background: 'linear-gradient(90deg, #4caf50, #8bc34a)'
                                  }}
                                ></div>
                              </div>
                            </div>
                            
                            <div className="text-[#a0a0a0] text-xs">{getTagSubTitle(idoData.type)}</div>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="text-center font-bold text-xl text-[#ff5722] mb-3">You missed a lot!</div>
                          <div className="bg-[#1a1a1a] rounded-lg p-3 border border-[#333333] mb-4">
                            <table className="w-full border-collapse">
                              <thead>
                                <tr className="border-b border-[#333333]">
                                  <th className="py-2 px-1 text-center text-[#d4d4d4] font-medium">Token</th>
                                  <th className="py-2 px-1 text-center text-[#d4d4d4] font-medium">MAX ROI</th>
                                  <th className="py-2 px-1 text-center text-[#d4d4d4] font-medium">Token</th>
                                  <th className="py-2 px-1 text-center text-[#d4d4d4] font-medium">MAX ROI</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td className="py-2 px-1 text-center text-[#d4d4d4] font-medium">kILO</td>
                                  <td className="py-2 px-1 text-center text-[#4caf50] font-bold">11.45x</td>
                                  <td className="py-2 px-1 text-center text-[#d4d4d4] font-medium">PARTI</td>
                                  <td className="py-2 px-1 text-center text-[#4caf50] font-bold">16.33x</td>
                                </tr>
                                <tr className="bg-[#222222]">
                                  <td className="py-2 px-1 text-center text-[#d4d4d4] font-medium">BR</td>
                                  <td className="py-2 px-1 text-center text-[#4caf50] font-bold">2.11x</td>
                                  <td className="py-2 px-1 text-center text-[#d4d4d4] font-medium">BMT</td>
                                  <td className="py-2 px-1 text-center text-[#4caf50] font-bold">15.82x</td>
                                </tr>
                                <tr>
                                  <td className="py-2 px-1 text-center text-[#d4d4d4] font-medium">SHELL</td>
                                  <td className="py-2 px-1 text-center text-[#4caf50] font-bold">34.51x</td>
                                  <td className="py-2 px-1 text-center text-[#d4d4d4] font-medium">BID</td>
                                  <td className="py-2 px-1 text-center text-[#4caf50] font-bold">10.45x</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </motion.div>

                </motion.div>
              </div>

              {/* 底部按钮组 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.6 }}
                className="mt-6"
              >
                <ShareButtons
                  cardId="ido-card"
                  data={idoData}
                  featureName="My Binance IDO Analysis"
                  shareText={`See my IDO analysis and then try yours: https://dapp.pinai.tech\nI participated in ${idoData?.participated_count || 0} IDO projects on Binance, with a total ROI of ${idoData?.roi || 0}x!`}
                  shareUrl="https://dapp.pinai.tech"
                />
              </motion.div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-[16px] shadow-lg p-6 text-center max-w-[345px] w-full mx-auto"
            >
              <p className="text-[#676C77] mb-4">
                No data available, please click the button below to start
                analysis
              </p>
              <motion.button
                onClick={() => fetchUserIDOData(bscAddress)}
                className="w-full bg-gradient-to-r from-[#f784ff] to-[#b784ff] text-white py-3 rounded-lg font-medium hover:opacity-90 transition-all shadow-md flex items-center justify-center"
                whileHover={{
                  scale: 1.03,
                  boxShadow: "0 10px 25px -5px rgba(247, 132, 255, 0.4)",
                }}
                whileTap={{ scale: 0.97 }}
              >
                Start Analysis
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default BinanceIDODetail;